<template>
  <div>
    <!-- Full-screen loading overlay -->
    <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
      </div>
    </v-overlay>

    <!-- Header với thống kê -->
    <v-row class="mb-6">
      <v-col cols="12" md="3">
        <v-card color="primary" dark>
          <v-card-text>
            <div class="text-h4">{{ stats.totalMedia || 0 }}</div>
            <div>Tổng Media</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card color="success" dark>
          <v-card-text>
            <div class="text-h4">{{ stats.totalImages || 0 }}</div>
            <div>Hình ảnh</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card color="info" dark>
          <v-card-text>
            <div class="text-h4">{{ stats.totalVideos || 0 }}</div>
            <div>Video</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card color="warning" dark>
          <v-card-text>
            <div class="text-h4">{{ formatFileSize(stats.totalSize) }}</div>
            <div>Tổng dung lượng</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Upload Section -->
    <v-card class="mb-6">
      <v-card-title>
        <v-icon class="mr-2">mdi-cloud-upload</v-icon>
        Upload Media
      </v-card-title>
      <v-card-text>
        <v-form @submit.prevent="uploadMedia" ref="uploadForm" v-model="uploadValid">
          <v-row>
            <v-col cols="12" md="6">
              <v-file-input
                v-model="uploadForm.media"
                label="Chọn file media"
                accept="image/*,video/*"
                :rules="[v => !!v || 'Vui lòng chọn file']"
                show-size
                prepend-icon="mdi-camera"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="uploadForm.productId"
                :items="products"
                item-title="name"
                item-value="id"
                label="Sản phẩm"
                :rules="[v => !!v || 'Vui lòng chọn sản phẩm']"
                clearable
              />
            </v-col>
          </v-row>
          <v-btn
            type="submit"
            color="primary"
            :loading="uploading"
            :disabled="!uploadValid"
          >
            <v-icon left>mdi-upload</v-icon>
            Upload
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Filters -->
    <v-card class="mb-6">
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.search"
              label="Tìm kiếm theo tên file"
              prepend-inner-icon="mdi-magnify"
              clearable
              @input="debouncedSearch"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.productId"
              :items="products"
              item-title="name"
              item-value="id"
              label="Lọc theo sản phẩm"
              clearable
              @update:model-value="loadMedia"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.mediaType"
              :items="mediaTypes"
              label="Loại media"
              clearable
              @update:model-value="loadMedia"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Media List -->
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-image-multiple</v-icon>
        Danh sách Media
        <v-spacer />
        <v-btn color="primary" @click="loadMedia">
          <v-icon left>mdi-refresh</v-icon>
          Làm mới
        </v-btn>
      </v-card-title>
      
      <v-data-table
        :headers="headers"
        :items="mediaList"
        :loading="loading"
        :items-per-page="pagination.limit"
        :page="pagination.page"
        :server-items-length="pagination.total"
        @update:page="onPageChange"
        @update:items-per-page="onItemsPerPageChange"
        class="elevation-1"
      >
        <!-- Media Preview -->
        <template v-slot:item.mediaPath="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="60" class="mr-3">
              <v-img
                v-if="item.mediaType === 'image'"
                :src="getMediaUrl(item.mediaPath)"
                :alt="item.originalName"
                cover
              />
              <v-icon v-else size="40" color="primary">mdi-video</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle2">{{ item.originalName }}</div>
              <div class="text-caption text-grey">{{ formatFileSize(item.fileSize) }}</div>
            </div>
          </div>
        </template>

        <!-- Media Type -->
        <template v-slot:item.mediaType="{ item }">
          <v-chip
            :color="item.mediaType === 'image' ? 'success' : 'info'"
            size="small"
          >
            <v-icon left size="small">
              {{ item.mediaType === 'image' ? 'mdi-image' : 'mdi-video' }}
            </v-icon>
            {{ item.mediaType === 'image' ? 'Hình ảnh' : 'Video' }}
          </v-chip>
        </template>

        <!-- Product Name -->
        <template v-slot:item.productName="{ item }">
          <v-chip
            v-if="item.productName"
            color="primary"
            size="small"
            @click="filterByProduct(item.productId)"
          >
            {{ item.productName }}
          </v-chip>
          <span v-else class="text-grey">Chưa gán sản phẩm</span>
        </template>

        <!-- Created Date -->
        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon="mdi-pencil"
            size="small"
            color="primary"
            @click="editMedia(item)"
          />
          <v-btn
            icon="mdi-delete"
            size="small"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>
      </v-data-table>
    </v-card>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-pencil</v-icon>
          Chỉnh sửa Media
        </v-card-title>
        <v-card-text>
          <v-form @submit.prevent="updateMedia" ref="editForm" v-model="editValid">
            <v-select
              v-model="editForm.productId"
              :items="products"
              item-title="name"
              item-value="id"
              label="Sản phẩm"
              :rules="[v => !!v || 'Vui lòng chọn sản phẩm']"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="editDialog = false">Hủy</v-btn>
          <v-btn
            color="primary"
            @click="updateMedia"
            :loading="updating"
            :disabled="!editValid"
          >
            Cập nhật
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5">Xác nhận xóa</v-card-title>
        <v-card-text>
          Bạn có chắc chắn muốn xóa media "{{ selectedMedia?.originalName }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteDialog = false">Hủy</v-btn>
          <v-btn
            color="error"
            @click="deleteMedia"
            :loading="deleting"
          >
            Xóa
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import mediaService from '@/services/mediaService';
import productService from '@/services/productService';
import { BASE_STORAGE } from '@/config';

export default {
  name: 'Media',
  data() {
    return {
      // Loading states
      pageLoading: false,
      loading: false,
      uploading: false,
      updating: false,
      deleting: false,

      // Data
      mediaList: [],
      products: [],
      stats: {},

      // Pagination
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      },

      // Filters
      filters: {
        search: '',
        productId: null,
        mediaType: null
      },

      // Upload form
      uploadForm: {
        media: null,
        productId: null
      },
      uploadValid: false,

      // Edit form
      editForm: {
        productId: null
      },
      editValid: false,
      editDialog: false,
      selectedMedia: null,

      // Delete confirmation
      deleteDialog: false,

      // Table headers
      headers: [
        { title: 'Preview', key: 'mediaPath', sortable: false },
        { title: 'Tên file', key: 'originalName', sortable: false },
        { title: 'Loại', key: 'mediaType', sortable: false },
        { title: 'Sản phẩm', key: 'productName', sortable: false },
        { title: 'Dung lượng', key: 'fileSize', sortable: false },
        { title: 'Ngày tạo', key: 'createdAt', sortable: false },
        { title: 'Thao tác', key: 'actions', sortable: false }
      ],

      // Media types
      mediaTypes: [
        { title: 'Hình ảnh', value: 'image' },
        { title: 'Video', value: 'video' }
      ],

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },

      // Search debounce
      searchTimeout: null
    };
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      this.pageLoading = true;
      try {
        await Promise.all([
          this.loadMedia(),
          this.loadStats(),
          this.loadProducts()
        ]);
      } catch (error) {
        this.showSnackbar('Lỗi khi tải dữ liệu', 'error');
        console.error('Error loading data:', error);
      } finally {
        this.pageLoading = false;
      }
    },

    async loadMedia() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.filters
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === '') {
            delete params[key];
          }
        });

        const response = await mediaService.getMediaList(params);
        
        // Xử lý response có thể có cấu trúc khác nhau
        if (response.data.success && response.data.data) {
          this.mediaList = response.data.data.data || [];
          this.pagination.total = response.data.data.total || 0;
          this.pagination.totalPages = response.data.data.totalPages || 0;
        } else if (response.data && Array.isArray(response.data)) {
          // Fallback nếu API trả về array trực tiếp
          this.mediaList = response.data;
          this.pagination.total = response.data.length;
          this.pagination.totalPages = 1;
        }
      } catch (error) {
        this.showSnackbar('Lỗi khi tải danh sách media', 'error');
        console.error('Error loading media:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      try {
        const response = await mediaService.getMediaStats();
        if (response.data.success && response.data.data) {
          this.stats = response.data.data;
        } else if (response.data) {
          // Fallback nếu API trả về data trực tiếp
          this.stats = response.data;
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },

    async loadProducts() {
      try {
        const response = await productService.getProducts({ limit: 1000 });
        // API trả về data trực tiếp trong response.data
        if (response.data && Array.isArray(response.data)) {
          this.products = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.products = response.data.data;
        } else {
          this.products = [];
        }
      } catch (error) {
        console.error('Error loading products:', error);
        // Fallback to empty array if API fails
        this.products = [];
      }
    },

    async uploadMedia() {
      if (!this.uploadForm.media || !this.uploadForm.productId) {
        this.showSnackbar('Vui lòng chọn file và sản phẩm', 'error');
        return;
      }

      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append('media', this.uploadForm.media);
        formData.append('productId', this.uploadForm.productId);

        const response = await mediaService.uploadMedia(formData);
        
        if (response.data.success || response.data) {
          this.showSnackbar('Upload media thành công', 'success');
          this.uploadForm = { media: null, productId: null };
          this.$refs.uploadForm.reset();
          await this.loadMedia();
          await this.loadStats();
        }
      } catch (error) {
        this.showSnackbar(error.response?.data?.message || 'Lỗi khi upload media', 'error');
        console.error('Error uploading media:', error);
      } finally {
        this.uploading = false;
      }
    },

    editMedia(item) {
      this.selectedMedia = item;
      this.editForm.productId = item.productId;
      this.editDialog = true;
    },

    async updateMedia() {
      this.updating = true;
      try {
        const response = await mediaService.updateMedia(this.selectedMedia.id, {
          productId: this.editForm.productId
        });
        
        if (response.data.success || response.data) {
          this.showSnackbar('Cập nhật media thành công', 'success');
          this.editDialog = false;
          await this.loadMedia();
        }
      } catch (error) {
        this.showSnackbar(error.response?.data?.message || 'Lỗi khi cập nhật media', 'error');
        console.error('Error updating media:', error);
      } finally {
        this.updating = false;
      }
    },

    confirmDelete(item) {
      this.selectedMedia = item;
      this.deleteDialog = true;
    },

    async deleteMedia() {
      this.deleting = true;
      try {
        const response = await mediaService.deleteMedia(this.selectedMedia.id);
        
        if (response.data.success || response.data) {
          this.showSnackbar('Xóa media thành công', 'success');
          this.deleteDialog = false;
          await this.loadMedia();
          await this.loadStats();
        }
      } catch (error) {
        this.showSnackbar(error.response?.data?.message || 'Lỗi khi xóa media', 'error');
        console.error('Error deleting media:', error);
      } finally {
        this.deleting = false;
      }
    },

    onPageChange(page) {
      this.pagination.page = page;
      this.loadMedia();
    },

    onItemsPerPageChange(itemsPerPage) {
      this.pagination.limit = itemsPerPage;
      this.pagination.page = 1;
      this.loadMedia();
    },

    filterByProduct(productId) {
      this.filters.productId = productId;
      this.loadMedia();
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadMedia();
      }, 500);
    },

    getMediaUrl(mediaPath) {
      return `${BASE_STORAGE}/${mediaPath}`;
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      };
    }
  }
};
</script>

<style scoped>
.v-avatar {
  border-radius: 8px;
}
</style>
